// Floating idle-session countdown, mounted once in App.js next to
// InternetStatusIndicator. Purely a read+render layer over the primitives
// useSessionWatcher already uses (utils/session.js) — it does not do its
// own idle tracking, it just displays the same last_activity_at-derived
// countdown and offers a manual "renew" shortcut to the same refresh call
// useSessionWatcher makes on a schedule in the background.
import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, IconButton, Tooltip, CircularProgress } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import Swal from 'sweetalert2';
import { useApi } from 'hooks/useApi';
import {
  getIdleMs,
  getRefreshToken,
  getSessionConfig,
  getToken,
  recordActivity,
  redirectToSessionExpired,
  setSession,
} from 'utils/session';

const TICK_MS = 1000;

const STAGE_COLORS = {
  normal: '#018EA1', // brand teal
  warning: '#fb8c00', // warning
  critical: '#F44335', // error
};

// One split-flap-style digit. Keying the inner face on the digit's own
// value makes React remount it whenever the number changes, which retriggers
// the CSS "flipDown" mount animation — a lightweight stand-in for a real
// two-piece flip card that reads as "the number drops down into place".
function FlipDigit({ digit, color }) {
  return (
    <Box
      sx={{
        position: 'relative',
        width: '17px',
        height: '24px',
        borderRadius: '3px',
        backgroundColor: color,
        boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.15)',
        overflow: 'hidden',
        perspective: '80px',
        transition: 'background-color 0.4s ease',
        flexShrink: 0,
      }}
    >
      <Box
        key={digit}
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: "'Roboto Mono', 'Courier New', monospace",
          fontWeight: 700,
          fontSize: '14px',
          lineHeight: 1,
          color: '#fff',
          transformOrigin: 'center top',
          '@keyframes flipDown': {
            '0%': { transform: 'rotateX(-100deg)', opacity: 0.2 },
            '55%': { transform: 'rotateX(14deg)', opacity: 1 },
            '100%': { transform: 'rotateX(0deg)', opacity: 1 },
          },
          animation: 'flipDown 0.35s ease-out',
        }}
      >
        {digit}
      </Box>
      {/* Center crease, like a real split-flap card */}
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          right: 0,
          top: '50%',
          height: '1px',
          backgroundColor: 'rgba(0,0,0,0.3)',
        }}
      />
    </Box>
  );
}
FlipDigit.propTypes = {
  digit: PropTypes.number.isRequired,
  color: PropTypes.string.isRequired,
};

function FlipColon() {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '3px', px: '1px' }}>
      <Box sx={{ width: '3px', height: '3px', borderRadius: '50%', backgroundColor: '#fff' }} />
      <Box sx={{ width: '3px', height: '3px', borderRadius: '50%', backgroundColor: '#fff' }} />
    </Box>
  );
}

export default function SessionIdleTimer() {
  const { user } = useApi();
  const [, forceTick] = useState(0);
  const [renewing, setRenewing] = useState(false);
  const expiredFiredRef = useRef(false);

  useEffect(() => {
    const id = setInterval(() => forceTick((t) => t + 1), TICK_MS);
    return () => clearInterval(id);
  }, []);

  if (!getToken()) return null;

  const { idleTimeoutMinutes, warningThresholdMinutes, renewThresholdMinutes } = getSessionConfig();
  const idleSeconds = getIdleMs() / 1000;
  const remainingSeconds = Math.max(0, idleTimeoutMinutes * 60 - idleSeconds);

  if (remainingSeconds <= 0 && !expiredFiredRef.current) {
    expiredFiredRef.current = true;
    redirectToSessionExpired('Session expired due to inactivity.');
  }

  const isRenewStage = remainingSeconds <= renewThresholdMinutes * 60;
  const isWarning = remainingSeconds <= warningThresholdMinutes * 60;
  const stage = isRenewStage ? 'critical' : isWarning ? 'warning' : 'normal';
  const color = STAGE_COLORS[stage];

  const totalSeconds = Math.round(remainingSeconds);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  const digits = [
    Math.min(9, Math.floor(minutes / 10)),
    minutes % 10,
    Math.floor(seconds / 10),
    seconds % 10,
  ];

  const handleRenew = async () => {
    if (renewing) return;
    setRenewing(true);
    try {
      const refreshTokenVal = getRefreshToken();
      if (!refreshTokenVal) throw new Error('No refresh token');
      const res = await user.refreshToken(refreshTokenVal);
      setSession({
        token: res.data.token,
        refresh_token: res.data.refresh_token,
        session_config: res.data.session_config,
      });
      recordActivity();
      Swal.fire({ icon: 'success', title: 'Session renewed', timer: 1200, showConfirmButton: false });
    } catch {
      redirectToSessionExpired('Session expired. Please log in again.');
    } finally {
      setRenewing(false);
    }
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        top: '16px',
        right: '140px',
        zIndex: 9997,
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
      }}
    >
      <Tooltip
        title={
          isRenewStage
            ? 'Session about to expire due to inactivity — click renew to stay logged in.'
            : isWarning
              ? 'Your session will expire soon due to inactivity.'
              : 'Time remaining before you are logged out due to inactivity.'
        }
        placement="left"
        arrow
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
            px: '8px',
            py: '5px',
            borderRadius: '8px',
            backgroundColor: '#333333',
            transition: 'background-color 0.4s ease',
            boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.25)',
            cursor: 'default',
          }}
        >
          <AccessTimeIcon sx={{ fontSize: '15px', color: '#fff' }} />
          <FlipDigit digit={digits[0]} color={color} />
          <FlipDigit digit={digits[1]} color={color} />
          <FlipColon />
          <FlipDigit digit={digits[2]} color={color} />
          <FlipDigit digit={digits[3]} color={color} />
        </Box>
      </Tooltip>
      {isRenewStage && (
        <Tooltip title="Renew session" placement="left" arrow>
          <span>
            <IconButton
              size="small"
              onClick={handleRenew}
              disabled={renewing}
              sx={{
                width: 28,
                height: 28,
                backgroundColor: 'error.main',
                color: 'white',
                boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.15)',
                '&:hover': { backgroundColor: 'error.dark' },
                '&.Mui-disabled': { backgroundColor: 'error.main', opacity: 0.6 },
              }}
            >
              {renewing ? <CircularProgress size={14} sx={{ color: 'white' }} /> : <AutorenewIcon sx={{ fontSize: 16 }} />}
            </IconButton>
          </span>
        </Tooltip>
      )}
    </Box>
  );
}
